// Per-user storage: routes all localStorage.* calls through the signed-in user namespace.
import { scopedStorage as localStorage } from '../services/userStorage';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Activity, Settings as SettingsIcon, Moon, Sun, Bell, User, Globe, Book, LogIn, LogOut, Sparkles, Mail, Volume2, ShieldCheck } from 'lucide-react';
import ScreenLayout from './ScreenLayout';
import { bibleTranslations, DEFAULT_BIBLE_TRANSLATION } from '../data/bibleTranslations';
import { useAuth } from '../context/AuthContext';
import { updateSubscription } from '../services/verseDeliveryService';
import AmbientPlayer from './AmbientPlayer';
import useDeliveryState from '../hooks/useDeliveryState';

const STORAGE_KEY = 'prayer_app_settings';
const defaultSettings = {
    theme: 'light',
    notifications: true,
    reminders: true,
    fontSize: 'medium',
    bibleVersion: DEFAULT_BIBLE_TRANSLATION,
    language: 'English'
};

const loadSettings = () => {
    try {
        return { ...defaultSettings, ...(JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}) };
    } catch {
        return defaultSettings;
    }
};

const Row = ({ title, description, children }) => (
    <div className="settings-row">
        <div className="settings-row-label">
            <h3>{title}</h3>
            <p>{description}</p>
        </div>
        <div className="settings-row-control">{children}</div>
    </div>
);

const Toggle = ({ checked, onChange, label }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        className={`settings-toggle ${checked ? 'is-on' : ''}`}
        onClick={onChange}
    >
        <span />
    </button>
);

const Settings = () => {
    const { configured, user, profile, plan, licenseStatus, startupIssue, isAdmin, signOut } = useAuth();
    const { subscriptions, setState: setDeliveryState, loading: deliveryLoading, error: deliveryError } = useDeliveryState();
    const [settings, setSettings] = useState(loadSettings);
    const subscription = subscriptions.find((item) => item.status === 'active') || subscriptions[0] || null;
    const [destination, setDestination] = useState('');
    const [destinationMessage, setDestinationMessage] = useState('');

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    }, [settings]);

    useEffect(() => {
        // The API deliberately returns only a masked hint. Never put that hint
        // in an editable field: it could otherwise be submitted as a new
        // credential and overwrite the encrypted destination.
        setDestination('');
    }, [subscription?.id, subscription?.destinationHint]);

    const handleToggle = (setting) => setSettings((prev) => ({ ...prev, [setting]: !prev[setting] }));
    const handleChange = (setting, value) => setSettings((prev) => ({ ...prev, [setting]: value }));
    const saveDestination = async (event) => {
        event.preventDefault();
        const value = destination.trim();
        if (!subscription || subscription.medium === 'email' || !value) return;
        setDestinationMessage('');
        try {
            setDeliveryState(await updateSubscription(subscription.id, { destination: value }));
            setDestinationMessage('Delivery destination saved.');
        } catch (error) {
            setDestinationMessage(error.message || 'Delivery destination could not be saved.');
        }
    };

    return (
        <ScreenLayout title="Settings" icon={SettingsIcon}>
            <section className="settings-hero">
                <p className="shared-kicker"><Sparkles size={14} /> Make it yours</p>
                <h1>Settings.</h1>
                <p>Manage your account and tune the app to fit the way you pray.</p>
            </section>

            <section className="settings-section">
                <header className="settings-section-head">
                    <span><Mail size={18} /></span>
                    <h2>Optional verse delivery</h2>
                </header>
                <div className="settings-section-body">
                    <Row title="Delivery schedules" description="Have a Bible verse emailed to you. Today’s prayer, Bible reader, and devotional are always ready in the app.">
                        <Link to="/prayer/schedule">Manage schedules</Link>
                    </Row>
                    {deliveryLoading ? (
                        <p className="settings-note" role="status">Loading your delivery settings…</p>
                    ) : deliveryError ? (
                        <p className="settings-note" role="alert">{deliveryError}</p>
                    ) : subscription ? (
                        <form className="settings-destination-form" onSubmit={saveDestination}>
                            {subscription.medium === 'email' ? (
                                <Row title="Delivery destination" description="Verified through your signed-in Google account">
                                    <span>{subscription.destinationHint}</span>
                                </Row>
                            ) : (
                                <Row title="Delivery destination" description="Where your Slack delivery arrives">
                                    <div className="settings-inline-edit">
                                        <input
                                            aria-label="Delivery destination"
                                            type="text"
                                            value={destination}
                                            placeholder="Enter a new Slack webhook URL"
                                            onChange={(event) => { setDestination(event.target.value); setDestinationMessage(''); }}
                                        />
                                        <button type="submit" disabled={!destination.trim()}>Save</button>
                                    </div>
                                </Row>
                            )}
                            {subscription.medium !== 'email' && subscription.destinationConfigured ? (
                                <p className="settings-note">
                                    Current destination: {subscription.destinationHint}. Enter the full destination only when you want to replace it.
                                </p>
                            ) : null}
                            {destinationMessage ? <p className="settings-save-message" role="status">{destinationMessage}</p> : null}
                            <p className="settings-note">Timing, pauses, and upcoming deliveries live in <Link to="/practice/deliveries">My Practice</Link>.</p>
                        </form>
                    ) : (
                        <div className="settings-empty-delivery">
                            <p>Choose a time to receive a verse at your signed-in email address.</p>
                            <Link to="/schedule?type=verse">Add a verse schedule</Link>
                        </div>
                    )}
                </div>
            </section>

            <section className="settings-section">
                <header className="settings-section-head">
                    <span><User size={18} /></span>
                    <h2>Account</h2>
                </header>
                <div className="settings-section-body">
                    {!configured ? (
                        <p className="settings-note">
                            Accounts aren’t set up yet. Add your Supabase keys to enable sign-in
                            (see <code>docs/SUPABASE_SETUP.md</code>).
                        </p>
                    ) : user ? (
                        <div className="settings-account">
                            <div className="settings-account-identity">
                                {profile?.avatar_url
                                    ? <img src={profile.avatar_url} alt="" className="settings-account-avatar" />
                                    : <span className="settings-account-avatar settings-account-avatar-fallback">{(profile?.full_name || user.email || 'U')[0].toUpperCase()}</span>}
                                <div>
                                    <p className="settings-account-name">{profile?.full_name || user.email}</p>
                                    <p className="settings-account-email">{user.email}</p>
                                </div>
                            </div>
                            <div className="settings-account-aside">
                                <span className="settings-plan-pill">{plan} plan · {licenseStatus}</span>
                                {isAdmin && (
                                    <Link to="/admin" className="settings-button-primary">
                                        <Activity size={15} /> Admin operations
                                    </Link>
                                )}
                                <button className="settings-button-ghost" onClick={signOut}>
                                    <LogOut size={15} /> Sign out
                                </button>
                            </div>
                            {startupIssue ? <p className="settings-note" role="status">{startupIssue}</p> : null}
                        </div>
                    ) : (
                        <div className="settings-account settings-account-signedout">
                            <p>Sign in to sync your journal, prayers, and saved guides across devices.</p>
                            <Link to="/login" className="settings-button-primary">
                                <LogIn size={16} /> Sign in with Google
                            </Link>
                        </div>
                    )}
                </div>
            </section>

            <section className="settings-section">
                <header className="settings-section-head">
                    <span><Volume2 size={18} /></span>
                    <h2>Sound &amp; ambience</h2>
                </header>
                <div className="settings-section-body settings-ambient-body">
                    <p className="settings-note">Choose a quiet background only when you want it. Sound never starts on its own.</p>
                    <AmbientPlayer />
                </div>
            </section>

            <section className="settings-section">
                <header className="settings-section-head">
                    <span><Sun size={18} /></span>
                    <h2>Appearance</h2>
                </header>
                <div className="settings-section-body">
                    <Row title="Theme" description="Choose between light and dark mode">
                        <div className="settings-segment">
                            <button className={settings.theme === 'light' ? 'is-active' : ''} onClick={() => handleChange('theme', 'light')}>
                                <Sun size={15} /> Light
                            </button>
                            <button className={settings.theme === 'dark' ? 'is-active' : ''} onClick={() => handleChange('theme', 'dark')}>
                                <Moon size={15} /> Dark
                            </button>
                        </div>
                    </Row>
                    <Row title="Text size" description="Adjust the size of text throughout the app">
                        <select className="settings-select" value={settings.fontSize} onChange={(e) => handleChange('fontSize', e.target.value)}>
                            <option value="small">Small</option>
                            <option value="medium">Medium</option>
                            <option value="large">Large</option>
                        </select>
                    </Row>
                </div>
            </section>

            <section className="settings-section">
                <header className="settings-section-head">
                    <span><Bell size={18} /></span>
                    <h2>Notifications</h2>
                </header>
                <div className="settings-section-body">
                    <Row title="Prayer reminders" description="Receive daily reminders to pray">
                        <Toggle checked={settings.reminders} onChange={() => handleToggle('reminders')} label="Toggle prayer reminders" />
                    </Row>
                    <Row title="Prayer request updates" description="Get notified about updates to prayer requests">
                        <Toggle checked={settings.notifications} onChange={() => handleToggle('notifications')} label="Toggle prayer request updates" />
                    </Row>
                </div>
            </section>

            <section className="settings-section">
                <header className="settings-section-head">
                    <span><Book size={18} /></span>
                    <h2>Bible preferences</h2>
                </header>
                <div className="settings-section-body">
                    <Row title="Bible version" description="Select your preferred Bible translation">
                        <select className="settings-select" value={settings.bibleVersion} onChange={(e) => handleChange('bibleVersion', e.target.value)}>
                            {bibleTranslations.map((translation) => (
                                <option key={translation.id} value={translation.id}>
                                    {translation.abbreviation} — {translation.name}
                                </option>
                            ))}
                        </select>
                    </Row>
                    <p className="settings-note">
                        Only verified, locally installed translations are listed. The World English Bible is public domain and ships with the app.
                    </p>
                </div>
            </section>

            <section className="settings-section">
                <header className="settings-section-head">
                    <span><Globe size={18} /></span>
                    <h2>Language</h2>
                </header>
                <div className="settings-section-body">
                    <Row title="App language" description="Choose your preferred language">
                        <select className="settings-select" value={settings.language} onChange={(e) => handleChange('language', e.target.value)}>
                            <option value="English">English</option>
                            <option value="Spanish">Spanish</option>
                            <option value="French">French</option>
                        </select>
                    </Row>
                </div>
            </section>

            <section className="settings-section">
                <header className="settings-section-head">
                    <span><ShieldCheck size={18} /></span>
                    <h2>Devotional content</h2>
                </header>
                <div className="settings-section-body">
                    <Row title="No ads. Ever." description="Verse Delivery never places advertising inside Scripture, devotionals, prayers, or journal entries.">
                        <span className="settings-policy-value">Always protected</span>
                    </Row>
                </div>
            </section>
        </ScreenLayout>
    );
};

export default Settings;
